/**
 * Analytics Portal V1 — the Events metric map (Phase 14, plan §4.6).
 *
 * Only keys the Phase 8 `GET /api/analytics/event` payload contains, and never a value this file
 * computes. CTA wording follows the approved terminology: Analytics measures the click on the
 * event's call-to-action and nothing beyond it, so no label or description here claims a completion,
 * a sale, a confirmed guest list or revenue. Add-to-calendar, event share, Event Copy Link, video and
 * livestream metrics are unsupported (C4) and have no entry here, so they can never render.
 * Interested-to-CTA conversion is absent from the payload, so it is not rendered (never computed).
 * `search_ctr` is present in the payload but deliberately not rendered in V1 (plan §12.12 A11-2).
 */
export const EVENT_HEADLINE = [
  { key: "visits", label: "Event page visits", hint: "One visit per session." },
  { key: "unique_visitors", label: "Unique visitors" },
  { key: "returning_visitors", label: "Returning visitors" },
  { key: "engagement_rate", label: "Engagement rate", suffix: "%" },
];

export const EVENT_GROUPS = [
  {
    id: "discovery",
    title: "Search & event-directory discovery",
    description: "How people came across this event.",
    metrics: [
      { key: "search_impressions", label: "Search impressions" },
      { key: "search_clicks", label: "Search clicks" },
      { key: "unique_search_clickers", label: "Unique search clickers" },
      { key: "directory_impressions", label: "Event-directory impressions" },
      { key: "directory_clicks", label: "Event-directory result clicks" },
      { key: "unique_directory_clickers", label: "Unique directory clickers" },
    ],
  },
  {
    id: "interest",
    title: "Interested activity",
    description: "The current total is live. Removals and net change are measured from "
      + "instrumentation, so each card states the date it has been measured from.",
    metrics: [
      { key: "interest_current", label: "Interested now" },
      { key: "interest_added", label: "Interested additions" },
      { key: "interest_removed", label: "Interested removals" },
      { key: "interest_net", label: "Net interested change" },
    ],
  },
  {
    id: "bookmarks",
    title: "Bookmarks",
    metrics: [
      { key: "bookmarks_current", label: "Bookmarks now" },
      { key: "bookmarks_added", label: "Bookmarks added" },
      { key: "bookmarks_removed", label: "Bookmarks removed" },
      { key: "bookmarks_net", label: "Net bookmarks" },
    ],
  },
  {
    id: "cta",
    title: "CTA performance",
    description: "A CTA click is a click on this event's call-to-action, which can lead to a "
      + "registration or ticketing page. Analytics measures only the click, never the completion.",
    metrics: [
      { key: "cta_clicks", label: "Event CTA clicks" },
      { key: "unique_cta_clickers", label: "Unique CTA clickers" },
      { key: "conversion_rate", label: "Visit-to-CTA conversion", suffix: "%" },
      { key: "unique_converters", label: "Unique converters" },
    ],
  },
  {
    id: "messaging",
    title: "Comments & questions",
    description: "Counts and metadata only — never comment, question or answer content.",
    metrics: [
      { key: "comments", label: "Comments" },
      { key: "questions", label: "Questions received" },
      { key: "questions_answered", label: "Questions answered" },
    ],
  },
  {
    id: "actions",
    title: "Organizer & internal actions",
    description: "Aggregate click counts only. The endpoint reports one internal total, so no "
      + "organizer-specific or per-destination split is shown, and no destination id, URL, organizer "
      + "contact detail or visitor identity is recorded or displayed.",
    metrics: [
      { key: "internal_clicks", label: "Internal link clicks" },
      { key: "outbound_clicks", label: "Outbound link clicks" },
    ],
  },
  {
    id: "engagement",
    title: "Engagement",
    metrics: [
      { key: "engagement_actions", label: "Engagement actions" },
      { key: "unique_engagers", label: "Unique engagers" },
    ],
  },
];
